/**
 * CPP (full Construction Phase Plan) wizard config — data-driven step
 * definitions.
 *
 * Phase 20 (Refactor 3, supplemented in Phase 20-02). Mirrors
 * `CPP_WIZARD_STEPS` (../wizard-schemas/cpp.ts) — 8 content steps + preview.
 * Site rules + welfare arrangements + the emergency facts (emergency
 * procedures, assembly point, nearest hospital) are single project-level facts
 * edited once in project settings (the former `rulesWelfare` and `emergency`
 * steps were removed).
 *
 * Step renderer assignment:
 *   - `context`       → data-driven (4 fields)
 *   - `description`   → data-driven (textarea)
 *   - `hazards`       → custom (`CppHazardBuilder`)
 *   - `controls`      → custom (`CppControlsBuilder`)
 *   - `organisation`  → data-driven (3 fields)
 *   - `hsPolicy`      → data-driven (textarea + AI)
 *   - `comms`         → data-driven (textarea)
 *   - `monitoring`    → data-driven (textarea + AI)
 *   - `preview`       → custom (`CppPreview`)
 *
 * AI section-fill is enabled for hsPolicy / monitoring
 * (Phase 09b CPP dispatcher coverage).
 */
import type { WizardConfig } from "./types";

export const CPP_WIZARD_CONFIG: WizardConfig = {
  documentType: "CPP",
  variant: "FULL",
  steps: [
    {
      id: "context",
      titleKey: "wizards.cpp.stepTitles.context",
      descriptionKey: "wizards.cpp.stepDescriptions.context",
      schemaRef: "CPP.steps.context",
      fields: [
        {
          id: "jobDescription",
          type: "text",
          labelKey: "wizards.rams.jobDescriptionLabel",
          required: true,
          multiline: true,
        },
        {
          id: "siteAddress",
          type: "text",
          labelKey: "wizards.rams.siteAddressLabel",
          required: true,
        },
        {
          id: "startDate",
          type: "date",
          labelKey: "wizards.rams.startDateLabel",
          helperKey: "wizards.rams.datePickerHelper",
        },
        {
          id: "endDate",
          type: "date",
          labelKey: "wizards.rams.endDateLabel",
          helperKey: "wizards.rams.dateFormatHelper",
        },
      ],
    },
    {
      id: "description",
      titleKey: "wizards.cpp.stepTitles.scope",
      descriptionKey: "wizards.cpp.stepDescriptions.scope",
      schemaRef: "CPP.steps.description",
      fields: [
        {
          id: "projectDescription",
          type: "textarea",
          labelKey: "wizards.cpp.fields.projectDescription",
          placeholderKey: "wizards.cpp.placeholders.projectDescription",
          required: true,
          rows: 5,
        },
      ],
    },
    // Site rules + welfare arrangements are single project-level facts edited
    // once in project settings (the former `rulesWelfare` step was removed) —
    // they render into the CPP from the project row.
    {
      id: "hazards",
      titleKey: "wizards.cpp.stepTitles.hazards",
      descriptionKey: "wizards.cpp.stepDescriptions.hazards",
      schemaRef: "CPP.steps.hazards",
      customRenderer: "CppHazardBuilder",
    },
    {
      id: "controls",
      titleKey: "wizards.cpp.stepTitles.controls",
      descriptionKey: "wizards.cpp.stepDescriptions.controls",
      schemaRef: "CPP.steps.controls",
      customRenderer: "CppControlsBuilder",
    },
    {
      id: "organisation",
      titleKey: "wizards.cpp.stepTitles.organisation",
      descriptionKey: "wizards.cpp.stepDescriptions.organisation",
      schemaRef: "CPP.steps.organisation",
      fields: [
        {
          id: "principalContractor",
          type: "text",
          labelKey: "wizards.cpp.fields.principalContractor",
          required: true,
        },
        {
          id: "worksSupervisorName",
          type: "text",
          labelKey: "wizards.cpp.fields.worksSupervisorName",
          required: true,
        },
        {
          id: "firstAidArrangements",
          type: "textarea",
          labelKey: "wizards.cpp.fields.firstAidArrangements",
          placeholderKey: "wizards.cpp.placeholders.firstAidArrangements",
          required: true,
          rows: 3,
        },
      ],
    },
    // Emergency procedures + assembly point + nearest hospital are single
    // project-level facts edited once in project settings (the former
    // `emergency` step was removed) — they render into the CPP from the project
    // row. (nearestHospital is geocoded once on project create.)
    {
      id: "hsPolicy",
      titleKey: "wizards.cpp.stepTitles.hsPolicy",
      descriptionKey: "wizards.cpp.stepDescriptions.hsPolicy",
      schemaRef: "CPP.steps.hsPolicy",
      fields: [
        {
          id: "healthAndSafetyPolicy",
          type: "textarea",
          labelKey: "wizards.cpp.fields.healthAndSafetyPolicy",
          placeholderKey: "wizards.cpp.placeholders.healthAndSafetyPolicy",
          required: true,
          rows: 5,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "CPP",
        sectionKey: "healthAndSafetyPolicy",
        targetFieldId: "healthAndSafetyPolicy",
      },
    },
    {
      id: "comms",
      titleKey: "wizards.cpp.stepTitles.comms",
      descriptionKey: "wizards.cpp.stepDescriptions.comms",
      schemaRef: "CPP.steps.comms",
      fields: [
        {
          id: "communicationProcedures",
          type: "textarea",
          labelKey: "wizards.cpp.fields.communicationProcedures",
          placeholderKey: "wizards.cpp.placeholders.communicationProcedures",
          required: true,
          rows: 4,
        },
      ],
    },
    {
      id: "monitoring",
      titleKey: "wizards.cpp.stepTitles.monitoring",
      descriptionKey: "wizards.cpp.stepDescriptions.monitoring",
      schemaRef: "CPP.steps.monitoring",
      fields: [
        {
          id: "monitoringAndReview",
          type: "textarea",
          labelKey: "wizards.cpp.fields.monitoringAndReview",
          placeholderKey: "wizards.cpp.placeholders.monitoringAndReview",
          required: true,
          rows: 4,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "CPP",
        sectionKey: "monitoringAndReview",
        targetFieldId: "monitoringAndReview",
      },
    },
    {
      id: "preview",
      titleKey: "wizards.cpp.stepTitles.preview",
      descriptionKey: "wizards.cpp.stepDescriptions.preview",
      schemaRef: "CPP.steps.preview",
      customRenderer: "CppPreview",
    },
  ],
};
